import type { QueryClient } from '@tanstack/react-query';

import { REALTIME_RECONNECTED, REALTIME_TOPIC_REVOKED, type RealtimeManager } from '@/shared/realtime';
import type { ModuleSetupContext } from '@/shared/registry/types';

import { calendarKeys, invalidateEventCards } from './api';
import { registerCalendarFormatters } from './lib/format';

/** Realtime event names of the calendar module (backend `CalendarRealtime`). */
export const CALENDAR_EVENTS = {
  eventChanged: 'calendar.event-changed',
  invitationsChanged: 'calendar.invitations-changed',
  calendarsChanged: 'calendar.calendars-changed',
  roomsChanged: 'calendar.rooms-changed',
} as const;

/**
 * Realtime events of other modules the calendar reacts to. The identity module sends `identity.absence-changed` (an
 * absence was recorded, changed or cancelled — by a person or a business process) to every client, so open planners
 * show the new days at once. Modules never import each other: the name is part of identity's realtime contract.
 */
export const IDENTITY_EVENTS = {
  absenceChanged: 'identity.absence-changed',
} as const;

export const calendarTopic = (calendarId: string) => `calendar:${calendarId}`;
export const roomTopic = (roomId: string) => `calendar-room:${roomId}`;

/** Which cached queries an event makes stale (pure, unit tested). */
export function staleKeysFor(type: string): (readonly unknown[])[] {
  switch (type) {
    case CALENDAR_EVENTS.eventChanged:
      return [
        calendarKeys.ranges,
        calendarKeys.events,
        calendarKeys.invitations,
        calendarKeys.counters,
        calendarKeys.freeBusy,
        [...calendarKeys.rooms, 'availability'],
      ];
    case CALENDAR_EVENTS.invitationsChanged:
      return [calendarKeys.invitations, calendarKeys.counters, calendarKeys.list];
    case CALENDAR_EVENTS.calendarsChanged:
      return [calendarKeys.list, calendarKeys.ranges];
    case CALENDAR_EVENTS.roomsChanged:
      return [calendarKeys.list, calendarKeys.rooms, calendarKeys.ranges];
    case IDENTITY_EVENTS.absenceChanged:
      return [calendarKeys.freeBusy];
    default:
      return [];
  }
}

/**
 * Keeps the calendar caches live: `calendar.*` events (to attendees, and on the topics of the calendars and rooms on
 * screen) invalidate what they affect, `identity.absence-changed` refreshes free/busy; after a reconnect everything is
 * refetched.
 */
export function registerCalendarRealtime(realtime: RealtimeManager, queryClient: QueryClient): () => void {
  const offEvents = realtime.on('calendar.*', (_payload, envelope) => {
    for (const queryKey of staleKeysFor(envelope.type)) {
      if (queryKey === calendarKeys.events) void invalidateEventCards(queryClient);
      else void queryClient.invalidateQueries({ queryKey });
    }
  });
  // Absences of colleagues are part of the scheduler: one recorded, moved or cancelled refreshes it.
  const offAbsences = realtime.on(IDENTITY_EVENTS.absenceChanged, () => {
    for (const queryKey of staleKeysFor(IDENTITY_EVENTS.absenceChanged))
      void queryClient.invalidateQueries({ queryKey });
  });
  // Lost access to a calendar (removed from its group, calendar no longer shared): open cards and views refetch
  // into what is left (an event card into its "not found" state).
  const offRevoked = realtime.on<{ topic?: string } | null>(REALTIME_TOPIC_REVOKED, (payload) => {
    if (payload?.topic?.startsWith('calendar:'))
      void queryClient.invalidateQueries({ queryKey: calendarKeys.all });
  });
  const offReconnect = realtime.on(REALTIME_RECONNECTED, () => {
    void queryClient.invalidateQueries({ queryKey: calendarKeys.all });
  });
  return () => {
    offEvents();
    offAbsences();
    offRevoked();
    offReconnect();
  };
}

/** Module setup: live caches and the `calendarWhen` / `calendarDate` formatters of notification texts. */
export function setupCalendar({ queryClient, realtime, i18n }: ModuleSetupContext): void {
  registerCalendarRealtime(realtime, queryClient);
  registerCalendarFormatters(i18n);
}
